import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Plus, ChevronLeft, ChevronRight, X,
  Users, User, MapPin, Clock, Edit, Trash2,
  XCircle, CheckCircle, AlertCircle, Calendar,
  Repeat, RefreshCw, RotateCcw,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const TRAININGSTYPEN = [
  { value: "krachttraining", label: "Krachttraining" },
  { value: "crossfit",       label: "CrossFit" },
  { value: "hiit",           label: "HIIT" },
  { value: "conditie",       label: "Conditie" },
  { value: "cardio",         label: "Cardio" },
  { value: "pilates",        label: "Pilates" },
  { value: "yoga",           label: "Yoga" },
  { value: "team_sport",     label: "Teamsport" },
  { value: "gemengd",        label: "Gemengd" },
  { value: "anders",         label: "Anders" },
];

// ── Constanten ────────────────────────────────────────────────────────────────
const PX_PER_MIN = 1.1;          // 66px per uur
const START_HOUR = 7;
const END_HOUR   = 22;
const GRID_HEIGHT = (END_HOUR - START_HOUR) * 60 * PX_PER_MIN; // ~990px
const HOUR_H     = 60 * PX_PER_MIN;

const TRAINER_COLORS = [
  "#3B82F6","#EF4444","#10B981","#8B5CF6",
  "#F59E0B","#EC4899","#14B8A6","#F97316",
  "#6366F1","#06B6D4","#A855F7","#84CC16",
];

function trainerColor(naam) {
  if (!naam?.trim()) return "#C9A84C";
  let h = 0;
  for (let i = 0; i < naam.length; i++) h = naam.charCodeAt(i) + ((h << 5) - h);
  return TRAINER_COLORS[Math.abs(h) % TRAINER_COLORS.length];
}

function isLight(hex) {
  const r = parseInt(hex.slice(1,3),16), g = parseInt(hex.slice(3,5),16), b = parseInt(hex.slice(5,7),16);
  return (r*299 + g*587 + b*114)/1000 > 150;
}

function timeToMin(t = "00:00") {
  const [h,m] = t.split(":").map(Number);
  return h * 60 + m;
}

function getMaandag(date = new Date()) {
  const d = new Date(date); d.setHours(0,0,0,0);
  const day = d.getDay();
  d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
  return d;
}

function addDays(d, n) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }
function toStr(d) { return d.toISOString().split("T")[0]; }
function fmtDag(d) { return d.toLocaleDateString("nl-NL", { weekday: "short", day: "numeric" }); }
function fmtMaand(d) { return d.toLocaleDateString("nl-NL", { month: "long" }); }
function fmtKop(d) { return d.toLocaleDateString("nl-NL", { weekday: "short", day: "numeric", month: "short" }); }

const WEEKDAGEN = ["ma","di","wo","do","vr","za","zo"];

// ── Trainer-kleur legenda ────────────────────────────────────────────────────
function TrainerLegenda({ trainers }) {
  if (!trainers.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mb-4" data-testid="trainer-legenda">
      {trainers.map(naam => {
        const bg = trainerColor(naam);
        return (
          <div key={naam} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
            style={{ background: bg + "22", border: `1px solid ${bg}55` }}>
            <div className="w-2 h-2 rounded-full shrink-0" style={{ background: bg }} />
            <span style={{ color: bg }}>{naam}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── Les-blok in de kalender ──────────────────────────────────────────────────
function LesBlok({ les, onClick }) {
  const bg      = les.status === "geannuleerd" ? "#555" : trainerColor(les.instructeur_naam);
  const txt     = isLight(bg) ? "#000" : "#fff";
  const startMin = timeToMin(les.start_tijd);
  const top      = (startMin - START_HOUR * 60) * PX_PER_MIN;
  const height   = Math.max(les.duur_minuten * PX_PER_MIN, 36);
  const isVol    = les.boekingen_count >= les.max_deelnemers;

  return (
    <button
      onClick={() => onClick(les)}
      data-testid={`les-blok-${les.id}`}
      style={{ top, height, background: bg, color: txt, position: "absolute", left: 1, right: 1, zIndex: 10 }}
      className="rounded-lg px-2 py-1 text-left overflow-hidden hover:brightness-110 active:scale-[0.98] transition-all shadow-md cursor-pointer"
    >
      <p className="font-black leading-none truncate" style={{ fontSize: height > 48 ? "11px" : "9px" }}>
        {les.serie_id && <span className="opacity-70 mr-0.5">↺ </span>}{les.naam}
      </p>
      {height > 38 && (
        <p className="opacity-85 truncate leading-tight mt-0.5" style={{ fontSize: "9px" }}>
          {les.start_tijd}
          {les.duur_minuten ? ` · ${les.duur_minuten}m` : ""}
        </p>
      )}
      {height > 54 && les.instructeur_naam && (
        <p className="opacity-75 truncate leading-tight" style={{ fontSize: "9px" }}>{les.instructeur_naam}</p>
      )}
      {height > 68 && (
        <p className="opacity-70 leading-tight" style={{ fontSize: "9px" }}>
          {les.boekingen_count}/{les.max_deelnemers}{isVol ? " VOL" : ""}
        </p>
      )}
    </button>
  );
}

// ── Tijdkolom ────────────────────────────────────────────────────────────────
function TijdKolom() {
  return (
    <div className="shrink-0 w-12 relative select-none" style={{ height: GRID_HEIGHT }}>
      {Array.from({ length: END_HOUR - START_HOUR + 1 }, (_, i) => {
        const uur = START_HOUR + i;
        return (
          <div key={uur} className="absolute right-2 flex items-start"
            style={{ top: i * HOUR_H - 8, height: HOUR_H }}>
            <span className="text-zinc-600 text-[10px] font-semibold tabular-nums">{String(uur).padStart(2,"0")}:00</span>
          </div>
        );
      })}
    </div>
  );
}

// ── Achtergrond rasterlijnen ──────────────────────────────────────────────────
function GridLines() {
  return (
    <div className="absolute inset-0 pointer-events-none">
      {Array.from({ length: END_HOUR - START_HOUR }, (_, i) => (
        <div key={i} className="absolute w-full border-t border-white/5"
          style={{ top: i * HOUR_H }} />
      ))}
    </div>
  );
}

// ── Week weergave (desktop) ───────────────────────────────────────────────────
function WeekView({ weekDagen, lessenByDatum, onLesClick, onDagClick }) {
  return (
    <div className="overflow-x-auto" data-testid="week-view">
      <div className="min-w-[720px]">
        {/* Dag headers */}
        <div className="flex border-b border-white/10 mb-0">
          <div className="w-12 shrink-0" />
          {weekDagen.map(dag => {
            const str = toStr(dag);
            const vandaag = str === toStr(new Date());
            return (
              <div key={str} className="flex-1 text-center py-2.5 cursor-pointer hover:bg-white/3 transition-colors"
                onClick={() => onDagClick(dag)}
                data-testid={`week-dag-${str}`}>
                <span className={`text-xs font-bold uppercase tracking-wide ${vandaag ? "text-[#C9A84C]" : "text-zinc-400"}`}>
                  {fmtKop(dag)}
                </span>
                {vandaag && <div className="w-1 h-1 rounded-full bg-[#C9A84C] mx-auto mt-1" />}
              </div>
            );
          })}
        </div>

        {/* Grid */}
        <div className="flex overflow-y-auto max-h-[70vh]" style={{ scrollBehavior: "smooth" }}>
          <TijdKolom />
          {weekDagen.map(dag => {
            const str = toStr(dag);
            const lessen = lessenByDatum[str] || [];
            return (
              <div key={str} className="flex-1 relative border-l border-white/5"
                style={{ height: GRID_HEIGHT }}>
                <GridLines />
                {lessen.map(les => (
                  <LesBlok key={les.id} les={les} onClick={onLesClick} />
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Dag weergave (mobiel) ─────────────────────────────────────────────────────
function DagView({ weekDagen, activeDag, setActiveDag, lessenByDatum, onLesClick }) {
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!scrollRef.current) return;
    const idx = weekDagen.findIndex(d => toStr(d) === toStr(activeDag));
    const el = scrollRef.current.children[idx];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  }, [activeDag, weekDagen]);

  const lessen = lessenByDatum[toStr(activeDag)] || [];

  return (
    <div data-testid="dag-view">
      {/* Dag tabs */}
      <div ref={scrollRef} className="flex gap-2 overflow-x-auto pb-2 mb-4 scrollbar-hide" style={{ scrollSnapType: "x mandatory" }}>
        {weekDagen.map(dag => {
          const str = toStr(dag);
          const isActive = str === toStr(activeDag);
          const vandaag = str === toStr(new Date());
          const heeftLessen = (lessenByDatum[str] || []).length > 0;
          return (
            <button key={str} onClick={() => setActiveDag(dag)}
              data-testid={`dag-tab-${str}`}
              style={{ scrollSnapAlign: "start" }}
              className={`shrink-0 flex flex-col items-center px-3.5 py-2 rounded-xl transition-all ${
                isActive
                  ? "bg-[#C9A84C] text-black"
                  : "bg-[#111] border border-white/8 text-zinc-400 hover:border-white/20"
              }`}>
              <span className="text-[10px] font-bold uppercase tracking-wide">
                {WEEKDAGEN[dag.getDay() === 0 ? 6 : dag.getDay() - 1]}
              </span>
              <span className={`text-lg font-black leading-none ${isActive ? "text-black" : vandaag ? "text-[#C9A84C]" : "text-white"}`}>
                {dag.getDate()}
              </span>
              {heeftLessen && !isActive && (
                <div className="w-1.5 h-1.5 rounded-full mt-0.5" style={{ background: "#C9A84C" }} />
              )}
            </button>
          );
        })}
      </div>

      {/* Dag tijdrooster */}
      <div className="overflow-y-auto max-h-[72vh]">
        {lessen.length === 0 ? (
          <div className="bg-[#111] border border-white/6 rounded-2xl p-10 text-center">
            <Calendar size={28} className="text-zinc-700 mx-auto mb-2" />
            <p className="text-zinc-500 text-sm font-semibold">
              {fmtDag(activeDag)} — geen lessen
            </p>
          </div>
        ) : (
          <div className="flex">
            <TijdKolom />
            <div className="flex-1 relative border-l border-white/5" style={{ height: GRID_HEIGHT }}>
              <GridLines />
              {lessen.map(les => (
                <LesBlok key={les.id} les={les} onClick={onLesClick} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Les detail / acties modal ─────────────────────────────────────────────────
function LesDetailModal({ les, token, onClose, onEdit, onAnnuleer, onDelete, onVerwijderSerie, onRefresh }) {
  const [boekingen, setBoekingen]         = useState([]);
  const [loadingBoek, setLoadingBoek]     = useState(false);
  const [showBoek, setShowBoek]           = useState(false);
  const [doorplannen, setDoorplannen]     = useState({});
  const [doorpSaving, setDoorpSaving]     = useState(null);

  const bg   = trainerColor(les.instructeur_naam);
  const isVol  = les.boekingen_count >= les.max_deelnemers;
  const isPast = les.datum < toStr(new Date());

  const loadBoekingen = async () => {
    setLoadingBoek(true); setShowBoek(true);
    const r = await fetch(`${API}/api/schedule/lessen/${les.id}/boekingen`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const d = await r.json();
    setBoekingen(d.boekingen || []);
    setLoadingBoek(false);
  };

  const handleDoorplannen = async (sporterId) => {
    setDoorpSaving(sporterId);
    try {
      const r = await fetch(`${API}/api/schedule/lessen/${les.id}/boekingen/doorplannen`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ sporter_id: sporterId }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Mislukt");
      setDoorplannen(p => ({ ...p, [sporterId]: d.ingepland }));
    } catch {}
    setDoorpSaving(null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/75 p-4"
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-[#0f0f0f] border border-white/10 rounded-2xl w-full max-w-md max-h-[88vh] overflow-y-auto">
        <div className="h-2 rounded-t-2xl" style={{ background: bg }} />
        <div className="flex items-start justify-between p-5 pb-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-white font-black text-lg leading-tight">{les.naam}</h3>
              {les.serie_id && (
                <span className="flex items-center gap-1 bg-[#C9A84C]/15 text-[#C9A84C] rounded-full px-2 py-0.5 text-[10px] font-bold">
                  <Repeat size={9} /> Herhalend
                </span>
              )}
            </div>
            <p className="text-zinc-400 text-sm mt-0.5">
              {new Date(les.datum).toLocaleDateString("nl-NL",{weekday:"long",day:"numeric",month:"long"})}
            </p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white mt-1"><X size={18} /></button>
        </div>

        <div className="px-5 pb-5 space-y-3">
          <div className="flex flex-wrap gap-2">
            <div className="flex items-center gap-1.5 bg-white/5 rounded-full px-3 py-1.5 text-xs text-zinc-300">
              <Clock size={11} /> {les.start_tijd} · {les.duur_minuten} min
            </div>
            {les.trainingstype && (
              <div className="bg-white/5 text-zinc-300 rounded-full px-3 py-1.5 text-xs font-semibold">
                {TRAININGSTYPEN.find(t => t.value === les.trainingstype)?.label || les.trainingstype}
              </div>
            )}
            <button onClick={() => !showBoek ? loadBoekingen() : setShowBoek(!showBoek)}
              data-testid={`les-detail-boekingen-${les.id}`}
              className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${
                isVol ? "bg-red-500/15 text-red-400" : "bg-[#C9A84C]/15 text-[#C9A84C]"
              }`}>
              <Users size={11} /> {les.boekingen_count}/{les.max_deelnemers}{isVol && " VOL"}
            </button>
            {les.wachtlijst_count > 0 && (
              <div className="bg-yellow-500/10 text-yellow-400 rounded-full px-3 py-1.5 text-xs font-bold">
                +{les.wachtlijst_count} wachtlijst
              </div>
            )}
            {les.credits_kosten > 0 && (
              <div className="bg-white/5 text-zinc-400 rounded-full px-3 py-1.5 text-xs">
                {les.credits_kosten} credits
              </div>
            )}
          </div>

          {les.instructeur_naam && (
            <div className="flex items-center gap-2 text-sm text-zinc-300">
              <div className="w-2 h-2 rounded-full shrink-0" style={{ background: bg }} />
              {les.instructeur_naam}
            </div>
          )}
          {les.locatie && (
            <div className="flex items-center gap-2 text-sm text-zinc-400">
              <MapPin size={13} className="shrink-0" /> {les.locatie}
            </div>
          )}
          {les.beschrijving && (
            <p className="text-zinc-500 text-sm leading-relaxed">{les.beschrijving}</p>
          )}
          {les.status === "geannuleerd" && (
            <div className="flex items-center gap-2 bg-red-500/10 text-red-400 rounded-xl px-4 py-2.5 text-sm font-semibold">
              <XCircle size={14} /> Les is geannuleerd
            </div>
          )}

          {/* Boekingen lijst + doorplannen */}
          {showBoek && (
            <div className="bg-[#1a1a1a] rounded-2xl p-4 mt-1">
              <p className="text-zinc-400 text-xs font-bold uppercase tracking-wide mb-3">Deelnemers</p>
              {loadingBoek ? (
                <p className="text-zinc-600 text-sm">Laden...</p>
              ) : boekingen.length === 0 ? (
                <p className="text-zinc-600 text-sm">Nog geen boekingen</p>
              ) : (
                <div className="space-y-2.5">
                  {boekingen.filter(b => b.status === "bevestigd").map(b => (
                    <div key={b.id} className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <User size={12} className="text-zinc-600 shrink-0" />
                        <span className="text-zinc-300 text-sm truncate">{b.sporter_naam || b.sporter_email}</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {b.credits_betaald > 0 && (
                          <span className="text-[#C9A84C] text-xs font-bold">{b.credits_betaald}cr</span>
                        )}
                        {les.serie_id && (
                          doorplannen[b.sporter_id] !== undefined ? (
                            <span className="text-emerald-400 text-[10px] font-bold">
                              +{doorplannen[b.sporter_id]} les{doorplannen[b.sporter_id] !== 1 ? "sen" : ""}
                            </span>
                          ) : (
                            <button
                              onClick={() => handleDoorplannen(b.sporter_id)}
                              disabled={doorpSaving === b.sporter_id}
                              data-testid={`doorplannen-${b.sporter_id}`}
                              className="flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-lg bg-[#C9A84C]/15 text-[#C9A84C] hover:bg-[#C9A84C]/25 transition-colors disabled:opacity-40">
                              <RefreshCw size={9} className={doorpSaving === b.sporter_id ? "animate-spin" : ""} />
                              Doorplannen
                            </button>
                          )
                        )}
                      </div>
                    </div>
                  ))}
                  {boekingen.filter(b => b.status === "wachtlijst").map((b,i) => (
                    <div key={b.id} className="flex items-center gap-2 opacity-60">
                      <span className="text-zinc-600 text-xs">W{i+1}</span>
                      <User size={12} className="text-zinc-600" />
                      <span className="text-zinc-400 text-sm">{b.sporter_naam || b.sporter_email}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Acties */}
          {les.status === "gepland" && !isPast && (
            <div className="flex gap-2 pt-2">
              <button onClick={() => { onClose(); onEdit(les); }}
                data-testid={`les-bewerken-${les.id}`}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-white/10 text-zinc-300 text-sm font-semibold hover:border-white/20 transition-all">
                <Edit size={13} /> Bewerken
              </button>
              <button onClick={() => onAnnuleer(les)}
                data-testid={`les-annuleer-${les.id}`}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-yellow-500/20 text-yellow-400 text-sm font-semibold hover:bg-yellow-500/8 transition-all">
                <XCircle size={13} />
              </button>
              <button onClick={() => onDelete(les)}
                data-testid={`les-verwijder-${les.id}`}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-red-500/20 text-red-400 text-sm font-semibold hover:bg-red-500/8 transition-all">
                <Trash2 size={13} />
              </button>
            </div>
          )}

          {/* Serie verwijderen */}
          {les.serie_id && les.status === "gepland" && !isPast && (
            <button onClick={() => onVerwijderSerie(les)}
              data-testid={`serie-verwijder-${les.id}`}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-red-500/15 text-red-400/70 text-xs font-semibold hover:bg-red-500/8 hover:text-red-400 transition-all">
              <RotateCcw size={11} /> Verwijder alle toekomstige lessen in deze serie
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Les aanmaken / bewerken modal ─────────────────────────────────────────────
function LesModal({ les, onClose, onSave, token }) {
  const isEdit = Boolean(les?.id);
  const [form, setForm] = useState({
    naam:             les?.naam || "",
    beschrijving:     les?.beschrijving || "",
    datum:            les?.datum || toStr(new Date()),
    start_tijd:       les?.start_tijd || "09:00",
    duur_minuten:     les?.duur_minuten || 60,
    max_deelnemers:   les?.max_deelnemers || 10,
    credits_kosten:   les?.credits_kosten ?? 0,
    instructeur_naam: les?.instructeur_naam || "",
    locatie:          les?.locatie || "",
    trainingstype:    les?.trainingstype || "",
    herhalen_weken:   1,
    herhalen_actief:  false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");
  const [serieActie, setSerieActie] = useState("deze"); // "deze" | "alle"

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Als trainingstype gekozen wordt, zet naam automatisch op label
  const handleTrainingstype = (value) => {
    const label = TRAININGSTYPEN.find(t => t.value === value)?.label || value;
    set("trainingstype", value);
    set("naam", label);
  };

  // Preview datums voor herhalende lessen
  const previewDatums = useMemo(() => {
    if (!form.herhalen_actief || form.herhalen_weken <= 1) return [];
    const result = [];
    const start = new Date(form.datum + "T12:00:00");
    for (let w = 0; w < Math.min(form.herhalen_weken, 8); w++) {
      const d = new Date(start);
      d.setDate(d.getDate() + w * 7);
      result.push(d.toLocaleDateString("nl-NL", { weekday: "short", day: "numeric", month: "short" }));
    }
    return result;
  }, [form.datum, form.herhalen_weken, form.herhalen_actief]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.naam || !form.datum || !form.start_tijd) {
      setError("Naam, datum en tijd zijn verplicht"); return;
    }
    setSaving(true); setError("");
    try {
      const payload = {
        ...form,
        duur_minuten:   +form.duur_minuten,
        max_deelnemers: +form.max_deelnemers,
        credits_kosten: +form.credits_kosten,
        herhalen_weken: form.herhalen_actief ? +form.herhalen_weken : 1,
      };
      delete payload.herhalen_actief;

      let url, method;
      if (isEdit && serieActie === "alle" && les.serie_id) {
        url = `${API}/api/schedule/serie/${les.serie_id}`;
        method = "PUT";
      } else if (isEdit) {
        url = `${API}/api/schedule/lessen/${les.id}`;
        method = "PUT";
      } else {
        url = `${API}/api/schedule/lessen`;
        method = "POST";
      }

      const r = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Opslaan mislukt");
      onSave();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const inp = "w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#C9A84C]/50 placeholder-zinc-600";
  const lbl = "text-zinc-400 text-xs font-semibold uppercase tracking-wide mb-1.5 block";

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/75 p-4"
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-[#0f0f0f] border border-white/10 rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-white/8">
          <h3 className="text-white font-bold">{isEdit ? "Les bewerken" : "Nieuwe les"}</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">

          {/* Type les (= naam + trainingstype gecombineerd) */}
          <div>
            <label className={lbl}>Type les *</label>
            <div className="grid grid-cols-2 gap-2">
              {TRAININGSTYPEN.map(t => (
                <button key={t.value} type="button" onClick={() => handleTrainingstype(t.value)}
                  data-testid={`trainingstype-${t.value}`}
                  className={`py-2.5 px-3 rounded-xl text-sm font-semibold transition-all text-left ${form.trainingstype === t.value ? "text-black" : "bg-white/5 border border-white/8 text-zinc-400 hover:text-white"}`}
                  style={form.trainingstype === t.value ? { background: "#C9A84C" } : {}}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Aangepaste naam (optioneel) */}
          <div>
            <label className={lbl}>Aangepaste naam <span className="text-zinc-600 normal-case font-normal">(optioneel)</span></label>
            <input value={form.naam} onChange={e => set("naam", e.target.value)}
              placeholder={form.trainingstype ? TRAININGSTYPEN.find(t=>t.value===form.trainingstype)?.label || "" : "Automatisch ingevuld na type keuze"}
              className={inp} />
          </div>

          {/* Datum + Tijd */}
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>Datum *</label>
              <input type="date" value={form.datum} onChange={e => set("datum", e.target.value)} className={inp} /></div>
            <div><label className={lbl}>Starttijd *</label>
              <input type="time" value={form.start_tijd} onChange={e => set("start_tijd", e.target.value)} step="300" className={inp} /></div>
          </div>

          {/* Duur / Max / Credits */}
          <div className="grid grid-cols-3 gap-3">
            <div><label className={lbl}>Duur (min)</label>
              <input type="number" value={form.duur_minuten} onChange={e => set("duur_minuten", e.target.value)} min={15} max={300} className={inp} /></div>
            <div><label className={lbl}>Max deeln.</label>
              <input type="number" value={form.max_deelnemers} onChange={e => set("max_deelnemers", e.target.value)} min={1} max={200} className={inp} /></div>
            <div><label className={lbl}>Credits</label>
              <input type="number" value={form.credits_kosten} onChange={e => set("credits_kosten", e.target.value)} min={0} className={inp} /></div>
          </div>

          {/* Instructeur / Locatie */}
          <div><label className={lbl}>Instructeur</label>
            <input value={form.instructeur_naam} onChange={e => set("instructeur_naam", e.target.value)} placeholder="Naam trainer" className={inp} /></div>
          <div><label className={lbl}>Locatie</label>
            <input value={form.locatie} onChange={e => set("locatie", e.target.value)} placeholder="Zaal A, Studio 2..." className={inp} /></div>
          <div><label className={lbl}>Beschrijving</label>
            <textarea value={form.beschrijving} onChange={e => set("beschrijving", e.target.value)} rows={2} placeholder="Optioneel..." className={`${inp} resize-none`} /></div>

          {/* Wekelijks herhalen (alleen nieuw) */}
          {!isEdit && (
            <div className="bg-[#111] border border-white/8 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white text-sm font-bold">Wekelijks herhalen</p>
                  <p className="text-zinc-600 text-xs mt-0.5">Maak meerdere lessen in één keer aan</p>
                </div>
                <button type="button" onClick={() => set("herhalen_actief", !form.herhalen_actief)}
                  data-testid="herhalen-toggle"
                  className={`relative w-11 h-6 rounded-full transition-all ${form.herhalen_actief ? "" : "bg-white/10"}`}
                  style={{ background: form.herhalen_actief ? "#C9A84C" : "" }}>
                  <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${form.herhalen_actief ? "left-5" : "left-0.5"}`} />
                </button>
              </div>
              {form.herhalen_actief && (
                <>
                  <div>
                    <label className={lbl}>Aantal weken</label>
                    <div className="flex gap-2 flex-wrap">
                      {[2,3,4,6,8,12,24,52].map(n => (
                        <button key={n} type="button" onClick={() => set("herhalen_weken", n)}
                          data-testid={`weken-${n}`}
                          className={`w-12 h-9 rounded-xl text-sm font-bold transition-all ${form.herhalen_weken === n ? "text-black" : "bg-white/6 border border-white/10 text-zinc-400"}`}
                          style={form.herhalen_weken === n ? { background: "#C9A84C" } : {}}>
                          {n}
                        </button>
                      ))}
                    </div>
                  </div>
                  {previewDatums.length > 0 && (
                    <div>
                      <p className="text-zinc-500 text-xs font-semibold mb-1.5">
                        Preview — {form.herhalen_weken} lessen aanmaken:
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {previewDatums.map((d, i) => (
                          <span key={i} className="text-[10px] bg-[#C9A84C]/10 text-[#C9A84C] px-2 py-0.5 rounded-full font-semibold">{d}</span>
                        ))}
                        {form.herhalen_weken > 8 && (
                          <span className="text-[10px] text-zinc-600 px-2 py-0.5">+{form.herhalen_weken - 8} meer...</span>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Edit serie optie */}
          {isEdit && les?.serie_id && (
            <div className="bg-[#111] border border-[#C9A84C]/20 rounded-xl p-3">
              <p className="text-zinc-400 text-xs font-semibold mb-2">Wat wil je aanpassen?</p>
              <div className="flex gap-2">
                {[
                  { v: "deze", l: "Alleen deze les" },
                  { v: "alle", l: "Alle toekomstige lessen in serie" },
                ].map(o => (
                  <button key={o.v} type="button" onClick={() => setSerieActie(o.v)}
                    className={`flex-1 py-2 rounded-xl text-xs font-bold transition-all ${serieActie === o.v ? "text-black" : "bg-white/5 text-zinc-500"}`}
                    style={serieActie === o.v ? { background: "#C9A84C" } : {}}>
                    {o.l}
                  </button>
                ))}
              </div>
            </div>
          )}

          {error && <p className="text-red-400 text-sm">{error}</p>}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border border-white/10 text-zinc-400 text-sm font-semibold hover:border-white/20">Annuleren</button>
            <button type="submit" disabled={saving} data-testid="save-les-btn"
              className="flex-1 py-2.5 rounded-xl text-black text-sm font-bold disabled:opacity-40"
              style={{ background: "#C9A84C" }}>
              {saving ? "Opslaan..." : isEdit ? "Bijwerken" : form.herhalen_actief ? `${form.herhalen_weken} lessen aanmaken` : "Aanmaken"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Hoofd component ───────────────────────────────────────────────────────────
export default function RoosterTab({ token }) {
  const [lessen,      setLessen]      = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [weekStart,   setWeekStart]   = useState(() => getMaandag());
  const [activeDag,   setActiveDag]   = useState(() => new Date());
  const [detailLes,   setDetailLes]   = useState(null);
  const [editLes,     setEditLes]     = useState(null);
  const [showModal,   setShowModal]   = useState(false);
  const [toast,       setToast]       = useState(null);
  const [isMobile,    setIsMobile]    = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check(); window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/schedule/lessen`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await r.json();
      setLessen(d.lessen || []);
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const weekDagen = useMemo(() =>
    Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
  [weekStart]);

  const lessenByDatum = useMemo(() => {
    const map = {};
    lessen.forEach(l => {
      if (!map[l.datum]) map[l.datum] = [];
      map[l.datum].push(l);
    });
    return map;
  }, [lessen]);

  const trainers = useMemo(() => {
    const set = new Set(lessen.map(l => l.instructeur_naam).filter(Boolean));
    return [...set].sort();
  }, [lessen]);

  const weekRange = `${weekDagen[0].toLocaleDateString("nl-NL",{day:"numeric",month:"short"})} – ${weekDagen[6].toLocaleDateString("nl-NL",{day:"numeric",month:"short",year:"numeric"})}`;

  const prevWeek = () => setWeekStart(w => addDays(w, -7));
  const nextWeek = () => setWeekStart(w => addDays(w,  7));

  const handleAnnuleer = async (les) => {
    if (!window.confirm(`Les "${les.naam}" annuleren?`)) return;
    try {
      const r = await fetch(`${API}/api/schedule/lessen/${les.id}/annuleren`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }
      });
      if (!r.ok) throw new Error((await r.json()).detail);
      showToast("Les geannuleerd"); setDetailLes(null); load();
    } catch (e) { showToast(e.message, "error"); }
  };

  const handleDelete = async (les) => {
    if (!window.confirm(`Les "${les.naam}" verwijderen?`)) return;
    try {
      const r = await fetch(`${API}/api/schedule/lessen/${les.id}`, {
        method: "DELETE", headers: { Authorization: `Bearer ${token}` }
      });
      if (!r.ok) throw new Error((await r.json()).detail);
      showToast("Les verwijderd"); setDetailLes(null); load();
    } catch (e) { showToast(e.message, "error"); }
  };

  const handleVerwijderSerie = async (les) => {
    if (!window.confirm(`Alle toekomstige lessen in de serie "${les.naam}" verwijderen?`)) return;
    try {
      const r = await fetch(`${API}/api/schedule/serie/${les.serie_id}`, {
        method: "DELETE", headers: { Authorization: `Bearer ${token}` }
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Mislukt");
      showToast(`${d.verwijderd} lessen verwijderd`); setDetailLes(null); load();
    } catch (e) { showToast(e.message, "error"); }
  };

  return (
    <div>
      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest mb-0.5">Planning</p>
          <h2 className="text-xl font-bold text-white">Rooster</h2>
        </div>
        <button onClick={() => { setEditLes(null); setShowModal(true); }}
          data-testid="create-les-btn"
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-black font-bold text-sm hover:opacity-90 transition-opacity"
          style={{ background: "#C9A84C" }}>
          <Plus size={16} /> Nieuwe les
        </button>
      </div>

      {/* ── Week navigatie ── */}
      <div className="flex items-center justify-between mb-4">
        <button onClick={prevWeek} data-testid="prev-week-btn"
          className="flex items-center gap-1 text-sm font-semibold text-zinc-400 hover:text-white transition-colors px-3 py-2 rounded-xl hover:bg-white/5">
          <ChevronLeft size={16} /> Vorige
        </button>
        <span className="text-white text-sm font-bold">{weekRange}</span>
        <button onClick={nextWeek} data-testid="next-week-btn"
          className="flex items-center gap-1 text-sm font-semibold text-zinc-400 hover:text-white transition-colors px-3 py-2 rounded-xl hover:bg-white/5">
          Volgende <ChevronRight size={16} />
        </button>
      </div>

      {/* ── Trainer legenda ── */}
      <TrainerLegenda trainers={trainers} />

      {/* ── Kalender ── */}
      {loading ? (
        <div className="h-64 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-[#C9A84C]/40 border-t-[#C9A84C] rounded-full animate-spin" />
        </div>
      ) : (
        <div className="bg-[#0f0f0f] border border-white/6 rounded-2xl overflow-hidden">
          {isMobile ? (
            <div className="p-3">
              <DagView
                weekDagen={weekDagen}
                activeDag={activeDag}
                setActiveDag={setActiveDag}
                lessenByDatum={lessenByDatum}
                onLesClick={setDetailLes}
              />
            </div>
          ) : (
            <div className="p-4">
              <WeekView
                weekDagen={weekDagen}
                lessenByDatum={lessenByDatum}
                onLesClick={setDetailLes}
                onDagClick={(dag) => { setActiveDag(dag); setIsMobile(false); }}
              />
            </div>
          )}

          {/* Toggle view knop */}
          <div className="flex justify-center pb-3 pt-1">
            <button onClick={() => setIsMobile(m => !m)}
              data-testid="toggle-view-btn"
              className="text-xs text-zinc-600 hover:text-zinc-400 transition-colors px-4 py-1.5">
              {isMobile ? "Weekoverzicht tonen" : "Dagweergave tonen"}
            </button>
          </div>
        </div>
      )}

      {/* ── Toast ── */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-xl text-sm font-semibold shadow-2xl ${
          toast.type === "success"
            ? "bg-[#111] border border-[#C9A84C]/40 text-white"
            : "bg-red-950 border border-red-500/40 text-red-300"
        }`}>
          {toast.type === "success"
            ? <CheckCircle size={16} className="text-[#C9A84C]" />
            : <AlertCircle size={16} className="text-red-400" />}
          {toast.msg}
        </div>
      )}

      {/* ── Modals ── */}
      {detailLes && (
        <LesDetailModal
          les={detailLes}
          token={token}
          onClose={() => setDetailLes(null)}
          onEdit={(l) => { setEditLes(l); setShowModal(true); }}
          onAnnuleer={handleAnnuleer}
          onDelete={handleDelete}
          onVerwijderSerie={handleVerwijderSerie}
          onRefresh={load}
        />
      )}
      {showModal && (
        <LesModal
          les={editLes}
          token={token}
          onClose={() => { setShowModal(false); setEditLes(null); }}
          onSave={() => {
            setShowModal(false); setEditLes(null); load();
            showToast(editLes ? "Les bijgewerkt!" : "Les aangemaakt!");
          }}
        />
      )}
    </div>
  );
}
